import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { AnchorComponent } from "../../elements";
import MarketplaceActionModal from "../../../pages/mplacer/MarketplaceActionModal";

const MIN_WIDTHS = {
    "Product Code": "220px",
    "Title": "280px",
    "Selling Price $": "140px",
    "Delivery": "180px",
    "Marketplaces": "200px",
    "Action": "100px",
};

const wrapperStyle = {
    width: "100%",
    maxWidth: "100%",
    maxHeight: "60vh",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
};

const stickyHeadStyle = {
    position: "sticky",
    top: 0,
    zIndex: 3,
    background: "#2a70b8",
    whiteSpace: "nowrap",
    padding: "12px 15px",
};

const stickyActionHeadStyle = {
    ...stickyHeadStyle,
    right: 0,
    zIndex: 4,
};

const stickyActionCellStyle = {
    position: "sticky",
    right: 0,
    zIndex: 2,
    background: "#fff",
    boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.08)",
    whiteSpace: "nowrap",
    padding: "12px 15px",
};

const cellStyle = { whiteSpace: "nowrap", padding: "12px 15px" };

const codeCellStyle = { padding: "12px 15px", minWidth: "220px", verticalAlign: "middle" };

const dateStyle = {
    margin: "4px 0 0",
    fontSize: "12px",
    lineHeight: "1.3",
    color: "#6c757d",
    whiteSpace: "nowrap",
};

const isSoldMp = (mp) => mp?.status
    ? mp.status === "Sold"
    : mp?.showing === false || mp?.showing === 0;

const getMpStatus = (mp) => mp?.status || (mp?.showing === false || mp?.showing === 0 ? "Sold" : "Unsold");

export default function MplacerProductsTableComponent({
    thead,
    tbody,
    onRefresh,
    activeTab,
    currentPage,
    totalPages,
    onPageChange,
}) {
    const { t } = useContext(TranslatorContext);
    const [data, setData] = useState([]);
    const [mpModal, setMpModal] = useState(false);
    const [selectedMpData, setSelectedMpData] = useState(null);

    useEffect(() => {
        setData(tbody || []);
    }, [tbody]);

    const handleMarketplaceClick = (e, product, mp, displayStatus) => {
        e.preventDefault();
        if (isSoldMp(mp)) return;

        setSelectedMpData({
            product,
            marketplace: mp,
            hidePunchOrder: displayStatus === "Hide" || displayStatus === "Hidden",
        });
        setMpModal(true);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        let startPage = Math.max(1, currentPage - 1);
        let endPage = Math.min(totalPages, startPage + 3);

        if (endPage - startPage < 3 && totalPages >= 4) {
            if (startPage === 1) {
                endPage = Math.min(totalPages, 4);
            } else if (endPage === totalPages) {
                startPage = Math.max(1, totalPages - 3);
            }
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`btn btn-sm ${currentPage === i ? "btn-primary" : "btn-outline-secondary"}`}
                    style={{ minWidth: "35px", padding: "6px 12px" }}
                >
                    {i}
                </button>
            );
        }

        return (
            <div className="d-flex align-items-center gap-1 flex-wrap">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn btn-sm btn-outline-secondary"
                    style={{ padding: "6px 12px" }}
                >
                    Prev
                </button>

                {startPage > 1 && (
                    <>
                        <button
                            onClick={() => onPageChange(1)}
                            className="btn btn-sm btn-outline-secondary"
                            style={{ padding: "6px 12px" }}
                        >
                            1
                        </button>
                        {startPage > 2 && <span className="px-1 text-muted">...</span>}
                    </>
                )}

                {pages}

                {endPage < totalPages && (
                    <>
                        {endPage < totalPages - 1 && <span className="px-1 text-muted">...</span>}
                        <button
                            onClick={() => onPageChange(totalPages)}
                            className="btn btn-sm btn-outline-secondary"
                            style={{ padding: "6px 12px" }}
                        >
                            {totalPages}
                        </button>
                    </>
                )}

                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn btn-sm btn-outline-primary"
                    style={{ padding: "6px 12px" }}
                >
                    Next
                </button>
            </div>
        );
    };

    const lastIndex = (thead?.length || 0) - 1;
    const showEdit = activeTab === "new" || activeTab === "marketplaced";

    return (
        <div>
            <div style={wrapperStyle}>
                <table
                    className="mc-table product"
                    style={{
                        width: "100%",
                        minWidth: activeTab === "sold" ? "1300px" : "1150px",
                        borderCollapse: "collapse",
                        tableLayout: "auto",
                        marginBottom: 0,
                    }}
                >
                    <thead className="mc-table-head primary">
                        <tr>
                            {thead?.map((item, index) => (
                                <th
                                    key={index}
                                    style={{
                                        ...(index === lastIndex ? stickyActionHeadStyle : stickyHeadStyle),
                                        minWidth: MIN_WIDTHS[item] || "150px",
                                    }}
                                >
                                    {t(item)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="mc-table-body even">
                        {data.length > 0 ? (
                            data.map((item, index) => {
                                const pImage = process.env.REACT_APP_IMG_URL + (item?.card_img || item?.first_img || "");
                                const pTitle = item?.title || "N/A";
                                const pDescribe = item?.selling_style || "N/A";
                                const pCode = item?.product_code || "N/A";
                                const pReceiveDate = item?.recieve_date;
                                const pIssueDate = item?.issue_date;

                                const pricePresent = typeof item?.price === "object"
                                    ? item?.price?.present
                                    : (item?.price || item?.sell_price_dollar || 0);
                                const auctionMinPrice = item?.pricing?.auction_min_price ?? item?.auction_min_price;

                                const iconView = item?.action?.view || "visibility";
                                const iconEdit = item?.action?.edit || "edit";
                                const trackingNumber = item?.delivery?.tracking_number;
                                const courierName = item?.delivery?.courier_company?.name;
                                const hasSoldMarketplace = item?.marketPlace?.some(
                                    (marketplace) =>
                                        marketplace?.status === "Sold" ||
                                        (!marketplace?.status &&
                                            (marketplace?.showing === false || marketplace?.showing === 0))
                                );

                                return (
                                    <tr key={item?.id || index} style={{ verticalAlign: "middle" }}>
                                        <td style={codeCellStyle}>
                                            <div className="mc-table-check">{pCode}</div>
                                            {pIssueDate && <p style={dateStyle}>Issue Date: {pIssueDate}</p>}
                                            {pReceiveDate && <p style={dateStyle}>Listed Date: {pReceiveDate}</p>}
                                        </td>
                                        <td style={{ padding: "12px 15px" }}>
                                            <div
                                                className="mc-table-product md"
                                                style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: "200px" }}
                                            >
                                                {pImage && (
                                                    <img
                                                        src={pImage}
                                                        alt={pTitle}
                                                        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px", flexShrink: 0 }}
                                                    />
                                                )}
                                                <div className="mc-table-group">
                                                    <h6 style={{ margin: 0, fontSize: "14px", lineHeight: "1.2", whiteSpace: "nowrap" }}>
                                                        {pTitle}
                                                    </h6>
                                                    <p style={{ margin: 0, fontSize: "12px", color: "#6c757d", whiteSpace: "nowrap" }}>
                                                        {pDescribe}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={cellStyle}>
                                            <div className="mc-table-price">
                                                <p style={{ margin: 0 }}>{pricePresent}</p>
                                                {item?.selling_style === "Auction" && auctionMinPrice !== null && auctionMinPrice !== undefined && auctionMinPrice !== "" && (
                                                    <p className="small text-muted mb-0">
                                                        <strong>{t("Auction")}:</strong> ${auctionMinPrice}
                                                    </p>
                                                )}
                                            </div>
                                        </td>

                                        {activeTab === "sold" && (
                                            <td style={cellStyle}>
                                                <span className="badge bg-success">
                                                    {item?.delivery?.status || "N/A"}
                                                </span>
                                                {trackingNumber && (
                                                    <div className="small text-muted mt-1" style={{ fontSize: "11px" }}>
                                                        Tracking No: {trackingNumber}
                                                    </div>
                                                )}
                                                {courierName && (
                                                    <div className="small text-muted" style={{ fontSize: "11px" }}>
                                                        {courierName}
                                                    </div>
                                                )}
                                            </td>
                                        )}

                                        {item?.marketPlace?.length > 0 && (
                                            <td style={{ padding: "12px 15px", minWidth: "200px" }}>
                                                <div className="d-flex align-items-center gap-1 flex-wrap">
                                                    {item?.marketPlace?.length > 0 ? (
                                                        item.marketPlace.map((mp, i) => {
                                                            const rawStatus = getMpStatus(mp);
                                                            const displayStatus =
                                                                activeTab === "sold" && hasSoldMarketplace && rawStatus !== "Sold"
                                                                    ? "Hide"
                                                                    : rawStatus;
                                                            const isSold = displayStatus === "Sold";

                                                            let badgeClass = "bg-success";
                                                            if (isSold) badgeClass = "bg-danger";
                                                            else if (displayStatus === "Hide" || displayStatus === "Hidden") {
                                                                badgeClass = "bg-warning text-dark";
                                                            }

                                                            return (
                                                                <button
                                                                    key={i}
                                                                    type="button"
                                                                    disabled={isSold}
                                                                    onClick={(e) => handleMarketplaceClick(e, item, mp, displayStatus)}
                                                                    title={`${mp?.name} (${displayStatus})`}
                                                                    className={`border rounded p-1 shadow-sm d-inline-flex align-items-center gap-1 btn btn-link ${isSold ? "bg-light opacity-75" : "bg-white"}`}
                                                                    style={{
                                                                        textDecoration: "none",
                                                                        cursor: isSold ? "not-allowed" : "pointer",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {mp?.logo && (
                                                                        <img
                                                                            src={process.env.REACT_APP_IMG_URL + mp.logo}
                                                                            alt={mp?.name}
                                                                            style={{ width: "16px", height: "16px", objectFit: "contain" }}
                                                                        />
                                                                    )}
                                                                    <span
                                                                        className={`badge ${badgeClass}`}
                                                                        style={{ fontSize: "10px", padding: "3px 6px" }}
                                                                    >
                                                                        {displayStatus}
                                                                    </span>
                                                                </button>
                                                            );
                                                        })
                                                    ) : (
                                                        <span className="text-muted small">None</span>
                                                    )}
                                                </div>
                                            </td>

                                        )}
                                        <td style={stickyActionCellStyle}>
                                            <div className="mc-table-action d-flex align-items-center gap-2">
                                                <AnchorComponent
                                                    to="/mplacer-products-view"
                                                    state={{ item }}
                                                    title="View"
                                                    className="material-icons view"
                                                >
                                                    {iconView}
                                                </AnchorComponent>
                                                {showEdit && (
                                                    <AnchorComponent
                                                        to="/mplacer-products-edit"
                                                        state={{ item }}
                                                        title="Edit"
                                                        className="material-icons edit"
                                                    >
                                                        {iconEdit}
                                                    </AnchorComponent>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={thead?.length || 8} className="text-center py-4">
                                    {t("no_products_found")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3 px-2 flex-wrap gap-2">
                    <div className="text-muted small">
                        Showing page {currentPage} of {totalPages}
                    </div>
                    <div>{renderPagination()}</div>
                </div>
            )}

            <MarketplaceActionModal
                show={mpModal}
                onHide={() => setMpModal(false)}
                selectedData={selectedMpData}
                onSuccess={onRefresh}
                activeTab={activeTab}
            />
        </div>
    );
}